/**
 * Tabla de hallazgos físicos revelados por gestos médicos.
 *
 * Cada gesto (PALPATION, PERCUSSION, AUSCULTATION) tiene afinidad por sistemas
 * anatómicos específicos. Al aplicar el gesto sobre un nodo de ese sistema,
 * se revela un hallazgo físico adicional — la maniobra clínica equivalente en
 * el paciente real. Si el gesto no es compatible con el sistema, no aporta.
 *
 * Ejemplo: auscultar el corazón en el caso IAM revela R3 gallop (signo de
 * disfunción ventricular), palparlo no aporta nada relevante al cuadro.
 */

import { ClinicalNodeData, AnatomicalSystem } from '../types/medical';
import { SNOMED_CT_SYSTEM } from '../types/fhir';

export type Gesture = 'palpation' | 'percussion' | 'auscultation';

export interface GestureFinding {
  /** Nuevo nodo que se agrega al paciente al disparar el gesto. */
  node: ClinicalNodeData;
  /** Narrativa para mostrar en pantalla. */
  narrative: string;
}

/**
 * Mapeo: caseId → gesto → sistema anatómico → hallazgo revelado.
 * Usamos sistema (no nodeId) para que cualquier nodo cardiovascular responda
 * a auscultación cardíaca, sin depender de IDs específicos.
 */
export const GESTURE_FINDINGS: Record<string, Partial<Record<Gesture, Partial<Record<AnatomicalSystem, GestureFinding>>>>> = {
  iam: {
    auscultation: {
      cardiovascular: {
        narrative: 'Auscultas el precordio: R1R2 presentes, R3 gallop audible, soplo sistólico II/VI en foco mitral.',
        node: {
          id: 'gesture-aus-cardio',
          type: 'physical_exam',
          concept: { code: '37931006', display: 'R3 gallop + soplo mitral', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          valueString: 'R3 gallop, soplo sistólico II/VI mitral — disfunción VI + insuficiencia mitral aguda',
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 4, volatility: 0.2, timeCriticality: 0.7 },
          visualization: { baseColor: '#06B6D4', icon: '🩺', pulse: true },
          relationships: [],
        },
      },
      respiratory: {
        narrative: 'Auscultas los pulmones: crepitantes bibasales finos hasta tercio medio. Killip II.',
        node: {
          id: 'gesture-aus-pulm',
          type: 'physical_exam',
          concept: { code: '48348007', display: 'Crepitantes bibasales', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'Crepitantes bibasales finos — congestión pulmonar (Killip II)',
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 4, volatility: 0.2, timeCriticality: 0.6 },
          visualization: { baseColor: '#06B6D4', icon: '🫁' },
          relationships: [],
        },
      },
    },
  },

  sepsis: {
    palpation: {
      renal: {
        narrative: 'Palpas las fosas lumbares: puñopercusión lumbar derecha positiva — foco urinario confirmado.',
        node: {
          id: 'gesture-palp-lumbar',
          type: 'physical_exam',
          concept: { code: '102502003', display: 'Puñopercusión lumbar +', system: SNOMED_CT_SYSTEM },
          system: 'renal',
          valueString: 'Puñopercusión derecha positiva — pielonefritis aguda',
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 4, volatility: 0.2, timeCriticality: 0.8 },
          visualization: { baseColor: '#06B6D4', icon: '👋' },
          relationships: [],
        },
      },
    },
    auscultation: {
      cardiovascular: {
        narrative: 'Auscultas el corazón: taquicardia, sin soplos. Perfusión periférica disminuida.',
        node: {
          id: 'gesture-aus-cardio-sepsis',
          type: 'physical_exam',
          concept: { code: '37931006', display: 'Auscultación cardíaca', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          valueString: 'Taquicardia regular, sin soplos. Relleno capilar > 3 seg.',
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 4, volatility: 0.2, timeCriticality: 0.5 },
          visualization: { baseColor: '#06B6D4', icon: '🩺' },
          relationships: [],
        },
      },
    },
  },

  epoc: {
    auscultation: {
      respiratory: {
        narrative: 'Auscultas los pulmones: sibilancias espiratorias difusas, hipoventilación bilateral. Sin crepitantes focales.',
        node: {
          id: 'gesture-aus-pulm-epoc',
          type: 'physical_exam',
          concept: { code: '48348007', display: 'Auscultación pulmonar', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'Sibilancias espiratorias difusas + hipoventilación bilateral — obstrucción severa, sin foco neumónico',
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 4, volatility: 0.2, timeCriticality: 0.8 },
          visualization: { baseColor: '#06B6D4', icon: '🫁', pulse: true },
          relationships: [],
        },
      },
    },
    percussion: {
      respiratory: {
        narrative: 'Percutes el tórax: hiperresonancia bilateral — atrapamiento aéreo característico.',
        node: {
          id: 'gesture-perc-torax',
          type: 'physical_exam',
          concept: { code: '8422006', display: 'Hiperresonancia torácica', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'Hiperresonancia bilateral — hiperinsuflación dinámica',
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 4, volatility: 0.2, timeCriticality: 0.5 },
          visualization: { baseColor: '#06B6D4', icon: '👆' },
          relationships: [],
        },
      },
    },
  },
};
